import type {
  CartItem,
  DraftLineItem,
  DraftLineItemImage,
  Resolution,
  ResolutionOption,
  ReturnDraft,
  ReturnMode,
  ShippingAddress,
} from "../types";

export function createDraft(orderId: string, shopId: string): ReturnDraft {
  return {
    orderId,
    shopId,
    lineItems: [],
    returnMode: null,
    storeId: null,
    pickupDate: null,
    address: null,
    shippingRateId: null,
    shippingRateTitle: null,
    shippingRateAmount: null,
    paymentIntentId: null,
    paidReturnMethod: null,
    rebuyRemainingCreditMethod: null,
    rebuyItems: [],
  };
}

export function setRebuyItems(
  draft: ReturnDraft,
  rebuyItems: CartItem[],
): ReturnDraft {
  return { ...draft, rebuyItems };
}

export function setRebuyRemainingCreditMethod(
  draft: ReturnDraft,
  method: "refund" | "discount",
): ReturnDraft {
  return { ...draft, rebuyRemainingCreditMethod: method };
}

export function setNotifyCustomer(
  draft: ReturnDraft,
  notifyCustomer: boolean,
): ReturnDraft {
  return { ...draft, notifyCustomer };
}

export function setWithShippingLabel(
  draft: ReturnDraft,
  withShippingLabel: boolean,
): ReturnDraft {
  return { ...draft, withShippingLabel };
}

export function setReturnShippingPaid(
  draft: ReturnDraft,
  returnShippingPaid: boolean,
): ReturnDraft {
  return { ...draft, returnShippingPaid };
}

export function addLineItem(
  draft: ReturnDraft,
  item: DraftLineItem,
): ReturnDraft {
  const idx = draft.lineItems.findIndex(
    (li) =>
      li.lineItemId === item.lineItemId &&
      li.resolution === item.resolution &&
      li.exchangeVariantId === item.exchangeVariantId,
  );
  if (idx !== -1) {
    const updated = [...draft.lineItems];
    updated[idx] = item;
    return { ...draft, lineItems: updated };
  }
  return { ...draft, lineItems: [...draft.lineItems, item] };
}

export function updateLineItem(
  draft: ReturnDraft,
  lineItemId: string,
  patch: Partial<DraftLineItem>,
): ReturnDraft {
  const currentItem = draft.lineItems.find(
    (li) => li.lineItemId === lineItemId,
  );
  const updatedLineItems = draft.lineItems.map((li) =>
    li.lineItemId === lineItemId ? { ...li, ...patch } : li,
  );

  const wasExchangeCredit = currentItem?.resolution === "exchange-credit";
  const isStillExchangeCredit =
    (patch.resolution ?? currentItem?.resolution) === "exchange-credit";
  const noMoreExchangeCredit =
    wasExchangeCredit &&
    !isStillExchangeCredit &&
    !updatedLineItems.some((li) => li.resolution === "exchange-credit");

  return {
    ...draft,
    lineItems: updatedLineItems,
    ...(noMoreExchangeCredit ? { paymentIntentId: null, rebuyItems: [] } : {}),
  };
}

export function removeLineItem(
  draft: ReturnDraft,
  lineItemId: string,
): ReturnDraft {
  const removedItem = draft.lineItems.find(
    (li) => li.lineItemId === lineItemId,
  );
  const updatedLineItems = draft.lineItems.filter(
    (li) => li.lineItemId !== lineItemId,
  );

  const wasExchangeCredit = removedItem?.resolution === "exchange-credit";
  const noMoreExchangeCredit =
    wasExchangeCredit &&
    !updatedLineItems.some((li) => li.resolution === "exchange-credit");

  return {
    ...draft,
    lineItems: updatedLineItems,
    ...(noMoreExchangeCredit ? { paymentIntentId: null, rebuyItems: [] } : {}),
  };
}

export function setLineItemReason(
  draft: ReturnDraft,
  lineItemId: string,
  reasonId: string,
  comment: string | null,
): ReturnDraft {
  return updateLineItem(draft, lineItemId, { reasonId, comment });
}

export function setLineItemImages(
  draft: ReturnDraft,
  lineItemId: string,
  imageUrls: DraftLineItemImage[],
): ReturnDraft {
  return updateLineItem(draft, lineItemId, { imageUrls });
}

export function setLineItemResolution(
  draft: ReturnDraft,
  lineItemId: string,
  resolution: Resolution,
  resolutionOption: ResolutionOption | null,
  exchangeVariantId: string | null,
): ReturnDraft {
  return updateLineItem(draft, lineItemId, {
    resolution,
    resolutionOption,
    exchangeVariantId,
  });
}

export function setReturnMode(
  draft: ReturnDraft,
  returnMode: ReturnMode,
  storeId: string | null,
  pickupDate: string | null,
): ReturnDraft {
  return { ...draft, returnMode, storeId, pickupDate };
}

export function setAddress(
  draft: ReturnDraft,
  address: ShippingAddress,
): ReturnDraft {
  return { ...draft, address };
}

export function setShippingRate(
  draft: ReturnDraft,
  shippingRateId: string,
  shippingRateTitle?: string | null,
  shippingRateAmount?: number | null,
): ReturnDraft {
  return {
    ...draft,
    shippingRateId,
    shippingRateTitle: shippingRateTitle ?? null,
    shippingRateAmount: shippingRateAmount ?? null,
  };
}

export function setPaidReturnMethod(
  draft: ReturnDraft,
  method: "refund" | "discount" | null,
): ReturnDraft {
  return { ...draft, paidReturnMethod: method };
}

export function setPaymentIntent(
  draft: ReturnDraft,
  paymentIntentId: string,
): ReturnDraft {
  return { ...draft, paymentIntentId };
}

export function isDraftEmpty(draft: ReturnDraft): boolean {
  return draft.lineItems.length === 0;
}

export function hasIncompleteLineItems(draft: ReturnDraft): boolean {
  return draft.lineItems.some(
    (li) => li.reasonId === null || li.resolution === null,
  );
}

export function hasConflictingResolutions(draft: ReturnDraft): boolean {
  const resolutions = draft.lineItems
    .map((li) => li.resolution)
    .filter((r): r is Resolution => r !== null);
  if (resolutions.length <= 1) return false;
  const first = resolutions[0];
  return resolutions.some((r) => r !== first);
}
